import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import "./table.scss";
import { Button } from "@/components";
import DownloadIcon from "@/assets/icons/svg/download.svg?react";
import { RootState, store } from "../../store";
import { connect, ConnectedProps } from "react-redux";
import {
  updateIsFilter,
  updateIsClearFilter,
} from "@/store/media/media.action";

import {
  downloadAsCSV,
  Display_Value_Key as dvk,
  handleFilterChangeUtil,
} from "../Create/utils";
import Filters, { IFilterProps } from "./Filters";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

const PAGE_NUMBER_WORDS: Record<number, string> = {
  1: "One",
  2: "Two",
  3: "Three",
  4: "Four",
  5: "Five",
  6: "Six",
  7: "Seven",
  8: "Eight",
  9: "Nine",
  10: "Ten",
};

function getPageAction(pageNum: number): string {
  return PAGE_NUMBER_WORDS[pageNum] ?? String(pageNum);
}

export interface PaginationProps {
  total: number;
  onChange: (key: string, value: number) => void;
  isFilter?: boolean;
  ListMediaApi?: any;
  filters?: any;
  downloadCsv?: boolean;
  initialPage?: any;
  handleSetFilters?: any;
  isClearFilter?: any;
  width?: string;
  align?: string;
  showRowsPerPage?: boolean;
  rowsPerPageOptions?: number[];
  minRowsPerPage?: number;
  isSetinParams?: boolean;
}

const Pagination = ({
  onChange,
  total,
  isFilter,
  ListMediaApi,
  filters,
  downloadCsv = false,
  initialPage = 1,
  handleSetFilters,
  updateIsFilter,
  isClearFilter,
  updateIsClearFilter,
  width = "100%",
  align = "left",
  showRowsPerPage = true,
  rowsPerPageOptions = [50, 100, 150, 200],
  minRowsPerPage = 50,
  isSetinParams = false,
}: Props) => {
  const { pathname } = useLocation();
  const location = pathname.startsWith("/discover") ? "discover" : "content";
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : initialPage;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const pageParam = searchParams.get("rows");
    return pageParam ? parseInt(pageParam, 10) : minRowsPerPage;
  });
  const [pageCount, setPageCount] = useState(0);
  const windowSize = 4; // max visible pages at a time
  const [visibleStart, setVisibleStart] = useState(() =>
    Math.max(1, page - windowSize + 1)
  );

  useEffect(() => {
    setPageCount(Math.ceil(total / rowsPerPage));
  }, [total, rowsPerPage]);

  // Update visibleStart when page changes, but only if pageCount is valid
  useEffect(() => {
    if (pageCount > 0) {
      const newVisibleStart = Math.max(1, page - windowSize + 1);
      const maxStart = Math.max(1, pageCount - windowSize + 1);
      // Clamp to ensure we don't go beyond the available pages
      setVisibleStart(Math.min(newVisibleStart, maxStart));
    }
  }, [page, pageCount]);

  // Effect to handle URL page parameter changes
  useEffect(() => {
    // if (isFilter) return;
    // const pageParam = searchParams.get('page');
    const pageParam = filters?.page;
    const newPage = pageParam ? pageParam : initialPage;

    if (newPage !== page && newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
      onChange("page", newPage);
    }
  }, [filters?.page, page, pageCount, onChange]);

  useEffect(() => {
    if (filters?.rows) {
      setRowsPerPage(filters?.rows);
    } else {
      setRowsPerPage(minRowsPerPage);
    }
  }, [filters?.rows]);

  const onPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRows = Number(e.target.value);
    trackEvent("browse", {
      category: "pagination",
      entity: "rows per page",
      action: String(newRows),
      location,
      ...getTrackUserContext(),
    });
    setRowsPerPage(newRows);
    // onChange('rows', newRows);
    const updatedFilter = handleFilterChangeUtil(
      "rows",
      newRows,
      null,
      filters,
      updateIsFilter,
      updateIsClearFilter,
      dvk
    );
    handleSetFilters(updatedFilter);
    // setSearchParams((prev) => {
    //   prev.set('page', page.toString());
    //   prev.set('rows', newRows.toString());
    //   return prev;
    // });

    // Scroll to top when rows per page changes
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePageChange = (
    newPage: number,
    adjustWindow: boolean = false,
    pageAction?: string
  ) => {
    if (newPage < 1 || newPage > pageCount) return;
    trackEvent("browse", {
      category: "pagination",
      entity: "page no",
      action: pageAction ?? getPageAction(newPage),
      location,
      ...getTrackUserContext(),
    });
    setPage(newPage);
    // onChange('page', newPage);
    const updatedFilter = handleFilterChangeUtil(
      "page",
      newPage,
      null,
      filters,
      updateIsFilter,
      updateIsClearFilter,
      dvk
    );
    handleSetFilters(updatedFilter);
    if (adjustWindow) {
      const maxStart = Math.max(1, pageCount - windowSize + 1);
      const currentEnd = visibleStart + windowSize - 1;
      if (newPage < visibleStart) {
        setVisibleStart(Math.max(1, newPage));
      } else if (newPage > currentEnd) {
        setVisibleStart(Math.min(maxStart, newPage - (windowSize - 1)));
      }
    }
    // setSearchParams((prev) => {
    //   prev.set('page', newPage.toString());
    //   return prev;
    // });

    // Scroll to top when page changes
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Calculate visible page numbers using a fixed window starting at visibleStart
  const getVisiblePages = () => {
    const startPage = Math.max(1, Math.min(visibleStart, pageCount));
    const endPage = Math.min(pageCount, startPage + windowSize - 1);

    return Array.from(
      { length: Math.max(0, endPage - startPage + 1) },
      (_, i) => startPage + i
    );
  };

  // Calculate range display values
  const from = (page - 1) * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage, total);

  if (total === 0) return null;

  const handleDownloadCSV = async () => {
    const csvFilters = { ...filters };
    delete csvFilters.rows;
    delete csvFilters.page;
    csvFilters.rows = total;
    const response = await ListMediaApi({
      ...csvFilters,
      foreign_keys: ["socials", "ctbydata"],
    });
    const columns = [
      { key: "title", header: "Title" },
      { key: "desc", header: "Description" },
      { key: "category", header: "Category" },
      {
        key: "status",
        header: "Status",
        getValue: (item: any) => (item.status === 1 ? "Active" : "Inactive"),
      },
      {
        key: "ct",
        header: "Created On",
        getValue: (item: any) => new Date(item.ct).toLocaleDateString(),
      },
      { key: "views", header: "Views" },
    ];
    downloadAsCSV(response.data, "media_list.csv", columns);
  };

  return (
    <div
      className="table_pagination border-top z-1"
      style={{
        width: width || "100%",
        left: align === "right" ? "auto" : "0",
        right: align === "right" ? "0" : "auto",
      }}
    >
      <div
        className={`d-flex align-items-center gap-3 ${
          downloadCsv ? "justify-content-between" : " justify-content-end"
        }`}
      >
        {downloadCsv && (
          <div className="d-flex align-items-center">
            <span
              className="px-2 me-2"
              onClick={handleDownloadCSV}
              style={{ cursor: "pointer" }}
            >
              <Button
                className="btn d-flex align-items-center gap-1 hover-white H"
                type="outline-primary"
                icon={
                  <DownloadIcon
                    style={{ width: "16px", height: "16px", fill: "#0d6efd" }}
                  />
                }
                iconPosition="start"
              >
                <span>Download CSV</span>
              </Button>
            </span>
          </div>
        )}
        <div className="d-flex flex-row gap-3">
          {showRowsPerPage && (
            <div className="d-flex align-items-center gap-2">
              <span>Rows per page:</span>
              <select
                className="form-select form-select-sm"
                value={rowsPerPage}
                onChange={onPerPageChange}
                style={{ width: "66px" }}>
                {rowsPerPageOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
          <nav aria-label="Table pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link px-3 py-1"
                  onClick={() => handlePageChange(1, true, "First")}
                  aria-label="First"
                  title="Jump to First"
                >
                  <span aria-hidden="true">&laquo;</span>
                </button>
              </li>
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link px-3 py-1"
                  onClick={() => handlePageChange(page - 1, true, "Prev")}
                  aria-label="Previous"
                  title="Previous"
                >
                  <span aria-hidden="true">&lsaquo;</span>
                </button>
              </li>

              {getVisiblePages().map((num) => (
                <li
                  key={num}
                  className={`page-item ${page === num ? "active" : ""}`}
                >
                  <button
                    className="page-link px-3 py-1"
                    onClick={() =>
                      handlePageChange(num, false, getPageAction(num))
                    }
                  >
                    {num}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${page === pageCount ? "disabled" : ""}`}
              >
                <button
                  className="page-link px-3 py-1 fs-6"
                  onClick={() => handlePageChange(page + 1, true, "Next")}
                  aria-label="Next"
                  title="Next"
                >
                  <span aria-hidden="true">&rsaquo;</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
};
const mapState = (state: RootState) => ({});

const mapDispatch = {
  updateIsFilter,
  updateIsClearFilter,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & PaginationProps;
export default connector(Pagination);
